/* eslint-disable @eventmobi/no-direct-capacitor-import */
import type {
  BiometryType as BiometryTypeEnum,
  NativeBiometricPlugin,
} from '@capgo/capacitor-native-biometric';

import {
  BiometryLabel,
  type BiometricAvailability,
  type BiometricPluginCompat,
} from './definitions';

export * from './definitions';

type NativeBiometricModule = typeof import('@capgo/capacitor-native-biometric');

// Inlined to avoid a value import of `@capgo/capacitor-native-biometric` at
// module load time. A value import would pull in `@capacitor/core` and define
// `window.Capacitor` before `getVersion()` runs in `bridge/index.ts`, tripping
// the "App Update Required" gate on browsers.
const BIOMETRY_FACE_ID: BiometryTypeEnum = 2;
const BIOMETRY_TOUCH_ID: BiometryTypeEnum = 1;
const BIOMETRY_FINGERPRINT: BiometryTypeEnum = 3;
const BIOMETRY_FACE_AUTH: BiometryTypeEnum = 4;
const BIOMETRY_IRIS_AUTH: BiometryTypeEnum = 5;
const BIOMETRY_MULTIPLE: BiometryTypeEnum = 6;
const ACCESS_CONTROL_BIOMETRY_ANY = 2;

// Per-entry identifier for our Keychain slot. Capgo passes it through to
// Apple's `kSecAttrServer`. It's not a security boundary (the bundle ID is
// the actual scope) and it's not a real backend URL; it's just a stable
// lookup key inside our app's Keychain partition.
const KEYCHAIN_SERVER = 'login';

const unsupported = () =>
  Promise.reject(new Error('Biometric authentication is not supported on this build.'));

const stub: BiometricPluginCompat = {
  isAvailable: () => Promise.resolve({ available: false, biometryType: BiometryLabel.None }),
  prompt: unsupported,
  setCredentials: () => Promise.resolve(),
  getCredentials: unsupported,
  deleteCredentials: () => Promise.resolve(),
};

const mapBiometry = (type: BiometryTypeEnum): BiometryLabel => {
  switch (type) {
    case BIOMETRY_FACE_ID:
    case BIOMETRY_FACE_AUTH:
      return BiometryLabel.FaceId;
    case BIOMETRY_TOUCH_ID:
    case BIOMETRY_FINGERPRINT:
    case BIOMETRY_IRIS_AUTH:
    case BIOMETRY_MULTIPLE:
      return BiometryLabel.TouchId;
    default:
      return BiometryLabel.None;
  }
};

const wrap = (NativeBiometric: NativeBiometricPlugin): BiometricPluginCompat => ({
  isAvailable: async (): Promise<BiometricAvailability> => {
    try {
      const result = await NativeBiometric.isAvailable({ useFallback: false });
      return {
        available: !!result.isAvailable,
        biometryType: mapBiometry(result.biometryType),
      };
    } catch {
      return { available: false, biometryType: BiometryLabel.None };
    }
  },

  prompt: async reason => {
    // useFallback: false rejects device PIN/passcode as a substitute. Some
    // Android devices default to allowing a "Use PIN" path on biometric
    // prompts, which would let a non-biometric secret pass our gate. The
    // whole point of calling prompt() before setCredentials/getCredentials
    // is to require biometric specifically.
    await NativeBiometric.verifyIdentity({ reason, useFallback: false });
  },

  setCredentials: async ({ email, secret }) => {
    await NativeBiometric.setCredentials({
      username: email,
      password: secret,
      server: KEYCHAIN_SERVER,
      accessControl: ACCESS_CONTROL_BIOMETRY_ANY,
    });
  },

  getCredentials: async reason => {
    const { username, password } = await NativeBiometric.getSecureCredentials({
      server: KEYCHAIN_SERVER,
      reason,
    });
    return { email: username, secret: password };
  },

  deleteCredentials: async () => {
    await NativeBiometric.deleteCredentials({ server: KEYCHAIN_SERVER });
  },
});

// We are intentionally using a mutable export so that we can mimic the original import
// syntax for Capacitor plugins, while still dynamically loading the correct capacitor version.
// Don't remove or try to fix the skipcq, as this syntax is mandatory for the compatibility bridge.
// skipcq: JS-E1009
export let Biometric: BiometricPluginCompat = stub;

const load = (module?: NativeBiometricModule) => {
  Biometric = module ? wrap(module.NativeBiometric) : stub;
};

export default load;
